import { format } from 'date-fns';
import type { Lesson } from '@court-sync/shared';
import { formatTimeForTooltip, formatTimeHHMM } from './timeFormat';

export interface LessonCalendarTooltipContext {
  ageGroupLabelById?: Record<string, string>;
  programNameById?: Record<string, string>;
}

export function getLessonAgeGroupDisplayLabel(
  lesson: Pick<Lesson, 'ageGroupId' | 'ageGroup'>,
  ageGroupLabelById?: Record<string, string>
): string | null {
  if (lesson.ageGroupId) {
    const custom = ageGroupLabelById?.[lesson.ageGroupId];
    if (custom) return custom;
  }
  if (lesson.ageGroup && lesson.ageGroup !== 'all') {
    return lesson.ageGroup.charAt(0).toUpperCase() + lesson.ageGroup.slice(1);
  }
  return null;
}

export function getLessonProgramDisplayLabel(
  lesson: Pick<Lesson, 'programId'>,
  programNameById?: Record<string, string>
): string | null {
  if (!lesson.programId || !programNameById) return null;
  return programNameById[lesson.programId] ?? null;
}

export function getLessonOccurrenceMetaLabelParts(
  lesson: Lesson,
  ctx?: LessonCalendarTooltipContext
): string[] {
  const parts: string[] = [];
  const age = getLessonAgeGroupDisplayLabel(lesson, ctx?.ageGroupLabelById);
  const program = getLessonProgramDisplayLabel(lesson, ctx?.programNameById);
  if (age) parts.push(age);
  if (program) parts.push(program);
  return parts;
}

export function formatLessonOccurrenceMetaLabels(
  lesson: Lesson,
  ctx?: LessonCalendarTooltipContext
): string {
  const parts = getLessonOccurrenceMetaLabelParts(lesson, ctx);
  return parts.length > 0 ? ` · ${parts.join(' · ')}` : '';
}

export function lessonOccurrenceChipTooltip(
  occ: {
    lesson: Lesson;
    date: Date;
    startTime?: string;
    endTime?: string;
    status: 'scheduled' | 'cancelled' | 'postponed';
  },
  ctx?: LessonCalendarTooltipContext
): string {
  const statusLabel =
    occ.status === 'cancelled'
      ? 'Cancelled'
      : occ.status === 'postponed'
        ? 'Postponed'
        : 'Scheduled';
  const startRaw = occ.startTime ?? occ.lesson?.startTime ?? '09:00';
  const endRaw = occ.endTime ?? occ.lesson?.endTime ?? '10:00';
  const start = formatTimeHHMM(String(startRaw));
  const end = formatTimeHHMM(String(endRaw));
  const startLabel = formatTimeForTooltip(start);
  const endLabel = formatTimeForTooltip(end);
  const timePart =
    startLabel && endLabel ? ` · ${startLabel}–${endLabel}` : '';
  const metaPart = formatLessonOccurrenceMetaLabels(occ.lesson, ctx);
  return `${occ.lesson.name} — ${format(occ.date, 'EEE, MMM d')}${timePart}${metaPart} (${statusLabel})`;
}
